import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ContactPicker from '../components/ContactPicker';
import BarnPicker from '../components/BarnPicker';
import toast from 'react-hot-toast';

const DISCIPLINES = ['Dressage','Hunter','Jumper','Eventing','Western trail','Barrel racing','Roping','Reining','Endurance','Pleasure','Other'];
const GENDERS = [{ val: 'mare', label: 'Mare' }, { val: 'gelding', label: 'Gelding' }, { val: 'stallion', label: 'Stallion' }, { val: 'filly', label: 'Filly' }, { val: 'colt', label: 'Colt' }];
const FARRIER_INTERVALS = [{ val: 4, label: '4 weeks' }, { val: 6, label: '6 weeks' }, { val: 8, label: '8 weeks' }, { val: 10, label: '10 weeks' }, { val: 12, label: '12 weeks' }];

const steps = ['Basics', 'Care', 'Health', 'Profile'];

const ProgressBar = ({ step }) => (
  <div style={{ display: 'flex', gap: 4, padding: '0 16px 12px', background: 'var(--cr-green)' }}>
    {steps.map((s, i) => (
      <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? 'white' : 'rgba(255,255,255,0.3)' }} />
    ))}
  </div>
);

const AddHorsePage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [form, setForm] = useState({
    name: '', breed: '', age: '', gender: '', color: '', markings: '', date_of_birth: '', sire: '', dam: '', date_of_birth: '', sire: '', dam: '', date_of_birth: '', sire: '', dam: '', blanket_size: '', blanket_color: '', sheet_size: '', sheet_color: '',
    discipline: '', registration_number: '', microchip: '',
    vet_name: '', vet_phone: '', farrier_name: '', farrier_phone: '',
    farrier_interval_weeks: 6, feed_notes: '', barn_name: '', barn_location: '',
    bio: '', privacy: 'public',
  });

  // Use useCallback to prevent function recreation on every render
  const handleChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const setVal = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const canContinue = () => {
    if (step === 0) return form.name.trim().length > 0;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (photoFile) fd.append('avatar', photoFile);
      const { data } = await api.post('/horses', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${form.name} added!`);
      navigate(`/horses/${data.id}/health`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add horse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar" style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <button onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/horses')}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '4px 8px 4px 0' }}>
            ‹
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: 'white', fontSize: 17 }}>Add a horse — {steps[step]}</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 1 }}>Step {step + 1} of {steps.length}</p>
          </div>
          {step < steps.length - 1 && (
            <button onClick={() => setStep(s => s + 1)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 12 }}>
              Skip
            </button>
          )}
        </div>
        <ProgressBar step={step} />
      </div>

      <div style={{ padding: '16px 12px' }}>

        {/* STEP 0: BASICS */}
        {step === 0 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileRef.current.click(); }}
                style={{ width: 90, height: 90, borderRadius: '50%', border: photoPreview ? 'none' : '2px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', cursor: 'pointer', overflow: 'hidden', background: photoPreview ? 'none' : 'var(--bg-secondary)' }}>
                {photoPreview ? <img src={photoPreview} alt="Horse" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 32 }}>🐴</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--cr-green)', cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileRef.current.click(); }}>
                {photoPreview ? 'Change photo' : 'Add photo'}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
            </div>

            <div className="card">
              <div className="form-group">
                <label className="form-label">Horse name *</label>
                <input className="form-input" type="text" placeholder="e.g. Bella" value={form.name} onChange={handleChange('name')} autoComplete="off" />
              </div>
              <div className="form-group">
                <label className="form-label">Gender</label>
                <div className="chip-group">
                  {GENDERS.map(g => (
                    <div key={g.val} className={`chip ${form.gender === g.val ? 'selected' : ''}`} onClick={() => setVal('gender', g.val)}>{g.label}</div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Age</label>
                  <input className="form-input" type="number" placeholder="12" value={form.age} onChange={handleChange('age')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Breed</label>
                  <input className="form-input" type="text" placeholder="KWPB" value={form.breed} onChange={handleChange('breed')} autoComplete="off" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <input className="form-input" type="text" placeholder="Bay, chestnut, grey..." value={form.color} onChange={handleChange('color')} autoComplete="off" />
              </div>
                        <div className="form-group">
            <label className="form-label">Date of birth</label>
            <input type="date" className="form-input" value={form.date_of_birth || ''} onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sire</label>
              <input className="form-input" value={form.sire || ''} onChange={e => setForm(f => ({ ...f, sire: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Dam</label>
              <input className="form-input" value={form.dam || ''} onChange={e => setForm(f => ({ ...f, dam: e.target.value }))} />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Markings</label>
                <input className="form-input" type="text" placeholder="Star, snip, socks..." value={form.markings} onChange={handleChange('markings')} autoComplete="off" />
              </div>
            </div>

            <div className="card">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Discipline</label>
                <div className="chip-group">
                  {DISCIPLINES.map(d => (
                    <div key={d} className={`chip ${form.discipline === d ? 'selected' : ''}`} onClick={() => setVal('discipline', d)}>{d}</div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* STEP 1: CARE */}
        {step === 1 && (
          <>
            <div className="card">
              <h3 style={{ marginBottom: 14 }}>Veterinarian</h3>
              <div className="form-group">
                <label className="form-label">Vet name</label>
                <ContactPicker contactType="vet" value={form.vet_name} onChange={handleChange('vet_name')} onPick={(c) => setForm(prev => ({ ...prev, vet_name: c.name, vet_phone: c.phone || prev.vet_phone }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Vet phone</label>
                <input className="form-input" type="tel" placeholder="(210) 555-0100" value={form.vet_phone} onChange={handleChange('vet_phone')} />
              </div>
            </div>
            <div className="card">
              <h3 style={{ marginBottom: 14 }}>Farrier</h3>
              <div className="form-group">
                <label className="form-label">Farrier name</label>
                <ContactPicker contactType="farrier" value={form.farrier_name} onChange={handleChange('farrier_name')} onPick={(c) => setForm(prev => ({ ...prev, farrier_name: c.name, farrier_phone: c.phone || prev.farrier_phone }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Farrier phone</label>
                <input className="form-input" type="tel" placeholder="(210) 555-0200" value={form.farrier_phone} onChange={handleChange('farrier_phone')} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Shoeing interval</label>
                <div className="chip-group">
                  {FARRIER_INTERVALS.map(fi => (
                    <div key={fi.val} className={`chip ${form.farrier_interval_weeks === fi.val ? 'selected' : ''}`} onClick={() => setVal('farrier_interval_weeks', fi.val)}>{fi.label}</div>
                  ))}
                </div>
              </div>
            </div>
        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Blanketing</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Blanket size</label>
              <input className="form-input" placeholder="e.g. 78" value={form.blanket_size || ''} onChange={e => setForm(f => ({ ...f, blanket_size: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Blanket color</label>
              <input className="form-input" value={form.blanket_color || ''} onChange={e => setForm(f => ({ ...f, blanket_color: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sheet size</label>
              <input className="form-input" value={form.sheet_size || ''} onChange={e => setForm(f => ({ ...f, sheet_size: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sheet color</label>
              <input className="form-input" value={form.sheet_color || ''} onChange={e => setForm(f => ({ ...f, sheet_color: e.target.value }))} />
            </div>
          </div>
        </div>

            <div className="card">
              <h3 style={{ marginBottom: 14 }}>Home barn</h3>
              <div className="form-group">
                <label className="form-label">Barn name</label>
                <BarnPicker value={form.barn_name} onChange={handleChange('barn_name')} onPick={(b) => setForm(prev => ({ ...prev, barn_name: b.name, barn_location: b.location || prev.barn_location }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input className="form-input" type="text" placeholder="La Vernia, TX" value={form.barn_location} onChange={handleChange('barn_location')} autoComplete="off" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Feed & supplement notes</label>
                <textarea className="form-input" placeholder="e.g. 4lb Strategy AM + PM, 2 flakes coastal..." value={form.feed_notes} onChange={handleChange('feed_notes')} rows={3} />
              </div>
            </div>
          </>
        )}

        {/* STEP 2: HEALTH */}
        {step === 2 && (
          <>
            <div style={{ background: 'var(--cr-green-light)', border: '1px solid var(--cr-green)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: 12 }}>
              <p style={{ fontSize: 12, color: 'var(--cr-green-dark)', margin: 0 }}>💡 Log vaccines and set reminders after adding your horse. Skip if you don't have dates handy.</p>
            </div>
            <div className="card">
              <h3 style={{ marginBottom: 14 }}>ID & registration</h3>
              <div className="form-group">
                <label className="form-label">Registration number</label>
                <input className="form-input" type="text" placeholder="e.g. USEF 12345" value={form.registration_number} onChange={handleChange('registration_number')} autoComplete="off" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Microchip number</label>
                <input className="form-input" type="text" placeholder="15-digit chip number" value={form.microchip} onChange={handleChange('microchip')} autoComplete="off" />
              </div>
            </div>
          </>
        )}

        {/* STEP 3: COMMUNITY PROFILE */}
        {step === 3 && (
          <>
            <div className="card">
              <h3 style={{ marginBottom: 14 }}>Horse's community profile</h3>
              <div className="form-group">
                <label className="form-label">Bio</label>
                <textarea className="form-input" placeholder="Tell the community about your horse..." value={form.bio} onChange={handleChange('bio')} rows={3} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Privacy</label>
                {[{ val: 'public', label: 'Public', desc: 'Anyone can find and follow' }, { val: 'barn', label: 'Barn only', desc: 'Only your barn group can see' }, { val: 'private', label: 'Private', desc: 'Only you can see' }].map(p => (
                  <label key={p.val} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}>
                    <input type="radio" name="privacy" value={p.val} checked={form.privacy === p.val} onChange={() => setVal('privacy', p.val)} style={{ marginTop: 2, accentColor: 'var(--cr-green)' }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{p.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {step < steps.length - 1 ? (
          <button className="btn btn-primary btn-full btn-lg" onClick={() => setStep(s => s + 1)} disabled={!canContinue()} style={{ marginTop: 8 }}>
            Continue →
          </button>
        ) : (
          <button className="btn btn-primary btn-full btn-lg" onClick={handleSubmit} disabled={loading} style={{ marginTop: 8 }}>
            {loading ? 'Adding horse...' : `Add ${form.name || 'horse'} 🐴`}
          </button>
        )}
        {step === steps.length - 1 && (
          <button className="btn btn-secondary btn-full" onClick={handleSubmit} disabled={loading} style={{ marginTop: 8 }}>
            Save and add vaccines later
          </button>
        )}
      </div>
    </div>
  );
};

export default AddHorsePage;